import { DEFAULT_LANGUAGE_CODE, normalizeUiLanguageCode } from "./languages.js";
import { isCheckoutEnabledPlan, normalizeCheckoutPlan, normalizePlan } from "./plans.js";

export const PLAN_COMPARISON_PAGE_PATH = "/plans";
export const PLAN_COMPARISON_STATUS = Object.freeze({
  included: "included",
  limited: "limited",
  unavailable: "unavailable",
  soon: "soon",
});
export const PLAN_COMPARISON_DOCUMENT_TITLE = "Min plan comparison";

function cell(status, label) {
  return Object.freeze({ status, label });
}

const included = (label = "Included") => cell(PLAN_COMPARISON_STATUS.included, label);
const limited = (label) => cell(PLAN_COMPARISON_STATUS.limited, label);
const unavailable = (label = "Not available") => cell(PLAN_COMPARISON_STATUS.unavailable, label);
const soon = (label = "Included") => cell(PLAN_COMPARISON_STATUS.soon, label);

export function buildPlanComparisonPath(locale = DEFAULT_LANGUAGE_CODE) {
  const normalizedLocale = normalizeUiLanguageCode(locale);
  return normalizedLocale === DEFAULT_LANGUAGE_CODE
    ? PLAN_COMPARISON_PAGE_PATH
    : `/l/${encodeURIComponent(normalizedLocale)}${PLAN_COMPARISON_PAGE_PATH}`;
}

export function buildPlanComparisonReturnPath(locale = DEFAULT_LANGUAGE_CODE) {
  const normalizedLocale = normalizeUiLanguageCode(locale);
  const homePath = normalizedLocale === DEFAULT_LANGUAGE_CODE
    ? "/"
    : `/l/${encodeURIComponent(normalizedLocale)}`;
  return `${homePath}#pricing`;
}

export function buildPlanComparisonCtaHref(plan = "free") {
  const normalizedPlan = normalizePlan(plan, "");
  if (normalizedPlan === "free") {
    return "/link";
  }
  if (!isCheckoutEnabledPlan(normalizedPlan)) {
    return "";
  }
  const checkoutPlan = normalizeCheckoutPlan(normalizedPlan, "fun");
  const searchParams = new URLSearchParams();
  searchParams.set("selectedPlan", checkoutPlan);
  return searchParams.size > 0 ? `/link?${searchParams.toString()}` : "/link";
}

export function getPlanComparisonCardDetail(plan = {}) {
  const features = Array.isArray(plan.features) ? plan.features : [];
  const includedFeature = features.find((feature) => feature?.included && feature.detail);
  const fallbackFeature = features.find((feature) => feature?.detail);
  return includedFeature?.detail || fallbackFeature?.detail || "See exactly what each plan unlocks.";
}

export const PLAN_COMPARISON_ROWS = Object.freeze([
  {
    key: "whatsapp-api", feature: "WhatsApp API access",
    free: unavailable(), fun: unavailable(), pro: included(), agent: soon(),
    detail: "Connect your own AI agent with Pro. Your agent handles schedules and approvals.",
  },
  {
    key: "outbound-summaries",
    feature: "Voice notes you send",
    free: included(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Min can follow your own voice note with a written summary or transcript.",
  },
  {
    key: "inbound-summaries",
    feature: "Voice notes people send you",
    free: unavailable(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Fun and Pro can DM you a summary when someone sends you a voice note.",
  },
  {
    key: "full-transcripts",
    feature: "Full transcripts",
    free: unavailable(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Fun and Pro can send the full text, not only the short summary.",
  },
  {
    key: "summary-translation",
    feature: "Summaries in another language",
    free: limited("Outbound only"),
    fun: included("Inbound + outbound"),
    pro: included("Inbound + outbound"),
    agent: soon("Any monitored chat"),
    detail: "When a summary is sent, Min can translate it into the target language if that language is supported.",
  },
  {
    key: "source-transcripts",
    feature: "Transcripts in the original language",
    free: unavailable(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Transcripts stay in the spoken source language unless a supported translation rule applies.",
  },
  {
    key: "ui-languages",
    feature: "Website in your language",
    free: included(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Use the Min website in English or one of the supported local languages.",
  },
  {
    key: "home-languages",
    feature: "Choose your summary language",
    free: included(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Choose the language Min should use when it talks to you.",
  },
  {
    key: "chat-language",
    feature: "Different language per chat",
    free: included(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Set a different delivery language for a specific chat when needed.",
  },
  {
    key: "group-summaries",
    feature: "Group voice notes",
    free: limited("Outbound only"),
    fun: included("Global defaults"),
    pro: included("Custom controls"),
    agent: soon("Monitored groups"),
    detail: "Fun can use global defaults for groups. Pro can tune group behavior separately.",
  },
  {
    key: "approval-mode",
    feature: "Approve before send",
    free: unavailable(),
    fun: included(),
    pro: included(),
    agent: soon(),
    detail: "Turn Automatic off for incoming or outgoing voice notes when you want to react with Ⓜ️ before Min sends results.",
  },
  {
    key: "referrals",
    feature: "Free time from referrals",
    free: included("Earn weeks and months"),
    fun: included("Earn weeks and months"),
    pro: included("Earn weeks and months"),
    agent: soon("Earn weeks and months"),
    detail: "A week when they join. A month of their plan when they first pay.",
  },
  {
    key: "remove-branding",
    feature: "Remove Min branding",
    free: unavailable(),
    fun: unavailable(),
    pro: included("Can remove"),
    agent: soon("Custom"),
    detail: "The Ⓜ️ mark is Min's signature. Pro can remove Min branding from outputs.",
  },
  {
    key: "output-customization",
    feature: "Customize Min's defaults",
    free: limited("Default setup"),
    fun: limited("Basic defaults"),
    pro: included("Full customization"),
    agent: soon("Custom workflows"),
    detail: "Pro adds the broadest control over defaults, output style, and chat-specific behavior.",
  },
  {
    key: "group-controls",
    feature: "Separate group and personal settings",
    free: unavailable(),
    fun: limited("Global defaults"),
    pro: included(),
    agent: soon(),
    detail: "Fun uses global defaults for groups. Pro adds per-chat overrides.",
  },
  {
    key: "per-chat-controls",
    feature: "Per-chat controls",
    free: unavailable(),
    fun: unavailable(),
    pro: included(),
    agent: soon(),
    detail: "Pro can set different inbound, outbound, branding, and group behavior per chat.",
  },
  {
    key: "agent-monitoring",
    feature: "Autonomous chat monitoring",
    free: unavailable(),
    fun: unavailable(),
    pro: unavailable(),
    agent: soon("Agent only"),
    detail: "Agent is the higher-trust assistant for monitoring, escalation, digests, and follow-through.",
  },
  {
    key: "urgent-escalation",
    feature: "Urgent message alerts",
    free: unavailable(),
    fun: unavailable(),
    pro: unavailable(),
    agent: soon("Agent only"),
    detail: "Agent can surface messages that need action instead of letting them get buried.",
  },
  {
    key: "scheduled-digests",
    feature: "Scheduled digests",
    free: unavailable(),
    fun: unavailable(),
    pro: included("Your own agent"),
    agent: soon("Agent only"),
    detail: "Daily digests with your own agent",
  },
  {
    key: "reply-help",
    feature: "Reply help",
    free: unavailable(),
    fun: unavailable(),
    pro: unavailable(),
    agent: soon("Agent only"),
    detail: "Agent can help draft replies, suggest next steps, and close loops.",
  },
  {
    key: "broader-whatsapp-surfaces",
    feature: "Channels, status, and workflows",
    free: unavailable(),
    fun: unavailable(),
    pro: unavailable(),
    agent: soon("Agent only"),
    detail: "Agent is the planned tier for broader WhatsApp workflows beyond voice-note summaries.",
  },
]);

export const PLAN_COMPARISON_NOTES = Object.freeze([
  "Everyone starts with one free month of Fun.",
  "If someone sends you a Min link before you join, both of you get an extra month.",
  "After your included Fun time ends, you can fall back to Free, keep Fun, or upgrade to Pro.",
  "Agent is visible so the roadmap is clear, but it is not purchasable yet.",
]);
